import React, { useCallback, useMemo, useEffect, useState } from "react";
import PivotTableChartIcon from "@mui/icons-material/PivotTableChart";
import MergeIcon from "@mui/icons-material/Merge";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    DataGrid,
    GridCellParams,
    GridPreProcessEditCellProps,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridValueFormatterParams,
} from "@mui/x-data-grid";
import { useGetUserDataQuery } from "../auth/dataAccess";
import { Box, Button, Checkbox, Tooltip } from "@mui/material";
import { IPatient } from "../patients/patientsSlice";
import {
    CrfField,
    CrfFieldOptionDefaultType,
    CrfFieldType,
    CrfReading,
    CrfReadingValue,
    FieldOption,
    Laterality,
    OptionType,
} from "./nebula.service";
import { EditableOptions } from "./components/EditableOptions";
import { TextAreaEditCell } from "./components/TextAreaEditCell";
import { ColumnHeader } from "./components/ColumnHeader";
import { EditableGridCell, GridCell } from "./components/GridCell";
import { useAppSelector } from "../../app/hooks";
import { v4 as uuidv4 } from "uuid";
import { getSelectedTrial } from "../auth/login/loginSlice";
import useRoles from "../permissions/useRoles";
import DisplayText from "../../components/DisplayText/DisplayText";
import { useEditCRFMutation, useSetFinalMutation, useUnsetFinalMutation } from "../crf/dataAccess";
import { useSnackbar } from "notistack";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { DatePickerEditCell } from "./components/DatePickerEditCell";
import AdjudicationDialog from "./components/AdjudicationDialog";
import { hasAnyConflicts } from "./utils/conflictChecks";

// Define constants for column widths
const COLUMN_WIDTHS = {
    final: 75,
    user: 200,
    laterality: 80,
    adjudicationNeeded: 50,
};

// Base styles that don't change based on configurations
const baseStyles = {
    "width": "100%",
    "padding": 2,
    "& .nonEditable": {
        backgroundColor: "#29323f",
        color: "#9c9b9b",
    },
};

// Transposed specific styles
const transposedStyles = {
    "& .MuiDataGrid-columnHeaders": {
        "& .MuiDataGrid-columnHeadersInner": {
            "transform": "none !important",
            "& > div:first-child": {
                zIndex: 2,
                backgroundColor: "#202735",
            },
        },
    },
};

// Default styles for columns and cells
const DEFAULT_STYLES = {
    backgroundColor: "#202735",
    headerTransform: "none !important",
};

/**
 * Consolidated function to generate all table styles based on configuration
 */
const generateTableStyles = (
    userRoles: { isProjectManager: boolean; isSponsor: boolean },
    hasMultipleRowsPerLaterality: boolean,
    showAdjudicationColumn: boolean,
    isTransposed: boolean
) => {
    const { isProjectManager, isSponsor } = userRoles;
    const showFinalColumn = isProjectManager && hasMultipleRowsPerLaterality;
    const adjudicationOffset = showAdjudicationColumn ? COLUMN_WIDTHS.adjudicationNeeded : 0;

    // Calculate fixed column positions and z-indices
    const columnOrder: Array<{
        field: string;
        index: number;
        zIndex: number;
        left?: number;
        positionClass: string;
        borderRight?: string;
    }> = [];

    let columnIndex = 1;
    let leftPosition = 0;

    // Adjudication column (first if visible)
    if (showAdjudicationColumn) {
        columnOrder.push({
            field: "adjudicationNeeded",
            index: columnIndex,
            zIndex: 4,
            left: leftPosition,
            positionClass: `fixed-column-${columnIndex}`,
            borderRight: "none",
        });
        columnIndex++;
        leftPosition += COLUMN_WIDTHS.adjudicationNeeded;
    }

    // Final column
    if (showFinalColumn) {
        columnOrder.push({
            field: "final",
            index: columnIndex,
            zIndex: 3,
            left: leftPosition,
            positionClass: `fixed-column-${columnIndex}`,
            borderRight: "2px solid #515151",
        });
        columnIndex++;
        leftPosition += COLUMN_WIDTHS.final;
    }

    // User column (if not sponsor)
    if (!isSponsor) {
        columnOrder.push({
            field: "userEmail",
            index: columnIndex,
            zIndex: 2,
            left: leftPosition,
            positionClass: `fixed-column-${columnIndex}`,
            borderRight: "none",
        });
        columnIndex++;
        leftPosition += COLUMN_WIDTHS.user;
    }

    // Laterality column
    columnOrder.push({
        field: "laterality",
        index: columnIndex,
        zIndex: isSponsor ? 2 : 1,
        left: leftPosition,
        positionClass: `fixed-column-${columnIndex}`,
        borderRight: "2px solid #515151",
    });

    // Create a mapping of field names to their position classes for easy lookup
    const fieldToPositionClass = columnOrder.reduce((acc, column) => {
        acc[column.field] = column.positionClass;
        return acc;
    }, {} as Record<string, string>);

    // Generate cell positioning styles for sticky columns
    const positionStyles = columnOrder.reduce((styles, column) => {
        return {
            ...styles,
            [`& .${column.positionClass}`]: {
                position: "sticky",
                left: column.left,
                zIndex: column.zIndex,
                borderRight: column.borderRight || "none",
                backgroundColor: DEFAULT_STYLES.backgroundColor,
            },
        };
    }, {});

    // Generate header styles
    const headerColumnStyles = columnOrder.reduce(
        (styles, column) => {
            return {
                ...styles,
                [`& > div:nth-child(${column.index})`]: {
                    zIndex: column.zIndex,
                    backgroundColor: DEFAULT_STYLES.backgroundColor,
                    height: "100%",
                },
            };
        },
        { transform: DEFAULT_STYLES.headerTransform }
    );

    const headerStyles = {
        "& .MuiDataGrid-cell--editing": {
            position: "relative",
            overflow: "visible !important",
        },
        "& .MuiDataGrid-columnHeaders": {
            "& .MuiDataGrid-columnHeadersInner": {
                ...headerColumnStyles,
            },
        },
    };

    // Create a function to determine cell class names
    const getCellClassNameFn = (params: GridCellParams) => {
        let className = "";

        // Apply position class if this is a fixed column
        const positionClass = fieldToPositionClass[params.field];
        if (positionClass) {
            className += ` ${positionClass}`;
        }

        // Add editable class
        if (params.isEditable) {
            className += " editableCell";
        } else {
            className += " nonEditable";
        }

        return className;
    };

    // Generate list of fixed column fields for scroll handling
    const fixedColumnFields = columnOrder.map((col) => col.field);

    return {
        positionStyles,
        headerStyles,
        transposedStyles: isTransposed ? transposedStyles : {},
        getCellClassNameFn,
        fixedColumnFields,
    };
};

interface ICRFVisitTableProps {
    fields: CrfField[];
    data: CrfReading[];
    patient: IPatient;
    visit: {
        name: string;
        uuid: string;
        certification: boolean;
        final: boolean;
    };
}

const DisabledFinalCheckbox = ({ checked }: { checked: boolean }) => (
    <Tooltip title="Final status cannot be modified as the visit is complete">
        <span>
            <Checkbox
                checked={checked}
                disabled={true}
                icon={checked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                sx={{
                    "color": "grey.500",
                    "&.Mui-disabled": {
                        color: "grey.500",
                    },
                    "opacity": 0.7,
                }}
            />
        </span>
    </Tooltip>
);

// Add this new component near the top of the file with other components
const TwoLineHeader = ({
    visit,
    email,
    laterality,
    needsAdjudication,
}: {
    visit: { certification: boolean; name: string; uuid: string };
    email: string;
    laterality: string;
    needsAdjudication: boolean;
}) => (
    <Box sx={{ textAlign: "left" }}>
        <DisplayText type="bodySmall" text={email} variant="semiBold" />
        {!visit.certification && (
            <Box sx={{ display: "flex", alignItems: "stretch" }}>
                <DisplayText type="bodySmall" style={{ marginBottom: "4px" }} text={`${laterality.toUpperCase()}`} />
                {needsAdjudication && (
                    <Tooltip title="Adjudication required">
                        <WarningAmberIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
                    </Tooltip>
                )}
            </Box>
        )}
    </Box>
);

const CRFVisitTable: React.FC<ICRFVisitTableProps> = ({ fields, data, patient, visit }) => {
    const { userHasRole } = useRoles();
    const { enqueueSnackbar } = useSnackbar();
    const tableUniqueId = useMemo(() => `tableId-${uuidv4()}`, []);

    const selectedTrial = useAppSelector(getSelectedTrial);
    const { data: userData } = useGetUserDataQuery();

    const [unsetFinal] = useUnsetFinalMutation();
    const [setFinal] = useSetFinalMutation();
    const [editCRF] = useEditCRFMutation();

    // State to manage transpose
    const [isTransposed, setIsTransposed] = useState(false);

    // Add state for the adjudication dialog
    const [adjudicationDialogOpen, setAdjudicationDialogOpen] = useState(false);
    const [selectedLaterality, setSelectedLaterality] = useState<Laterality | null>(null);

    const renderEditCell =
        (field: CrfField, laterality?: string, email?: string) => (params: GridRenderEditCellParams) => {
            // Assign laterality from row if not provided
            laterality = laterality || (params.row.laterality as string);
            // Assign email from row if not provided
            const userEmail = email || (params.row.userEmail as string);

            if (field.fieldType === CrfFieldType.date) {
                const handleUpdate = (newValue: any) => {
                    const payload = {
                        laterality: laterality as Laterality,
                        visitId: visit.uuid,
                        email: userEmail,
                        data: [
                            {
                                uuid: field.uuid,
                                fieldName: field.fieldName,
                                fieldValue: newValue,
                            },
                        ],
                    };

                    return editCRF({ patientId: patient.uuid, crfUpdate: payload }).unwrap();
                };

                return (
                    <DatePickerEditCell
                        {...params}
                        email={userEmail}
                        visitId={visit.uuid}
                        patientId={patient.uuid}
                        laterality={laterality}
                        handleUpdate={handleUpdate}
                    />
                );
            } else if (field.fieldType === CrfFieldType.string) {
                const handleUpdate = (newValue: any) => {
                    const payload = {
                        laterality: laterality as Laterality,
                        visitId: visit.uuid,
                        email: userEmail,
                        data: [
                            {
                                uuid: field.uuid,
                                fieldName: field.fieldName,
                                fieldValue: newValue,
                            },
                        ],
                    };

                    return editCRF({ patientId: patient.uuid, crfUpdate: payload }).unwrap();
                };

                return (
                    <TextAreaEditCell
                        {...params}
                        email={userEmail}
                        visitId={visit.uuid}
                        patientId={patient.uuid}
                        fieldDefinition={field}
                        laterality={laterality}
                        handleUpdate={handleUpdate}
                    />
                );
            } else if (field.fieldType === CrfFieldType.list) {
                return (
                    <EditableOptions
                        {...params}
                        options={field.fieldOptions as FieldOption[]}
                        fieldDefinition={field}
                        laterality={laterality}
                        visitId={visit.uuid}
                        patientId={patient.uuid}
                        userEmail={userEmail}
                    />
                );
            }
        };

    const renderCell =
        (field: CrfField, laterality?: string, email?: string) =>
        (params: GridRenderCellParams<string, any, string>) => {
            // Assign laterality from row if not provided
            const lat = laterality || (params.row.laterality as string);
            // Assign email from row if not provided
            const userEmail = email || (params.row.userEmail as string);
            return (
                <EditableGridCell
                    {...params}
                    email={userEmail}
                    visitId={visit.uuid}
                    patientId={patient.uuid}
                    fieldDefinition={field}
                    laterality={lat}
                />
            );
        };

    const validateFixedOptions = (newValue: any, options: FieldOption[]) => {
        const fixedOptionsValues = options.filter(
            (option): option is CrfFieldOptionDefaultType => option.type !== OptionType.input
        );

        const optionValues = fixedOptionsValues.map((option) => option.value);

        return optionValues.includes(newValue);
    };

    const getFieldValidator = (field: CrfField) => (params: GridPreProcessEditCellProps) => {
        // Avoid showing error tooltip when user didn't type anything yet
        if (params.props.value === "") return { ...params };

        if (field.fieldType === CrfFieldType.date) {
            const isDate = new Date(params.props.value);
            return isDate ? { ...params } : { ...params, error: "Value must be a date." };
        }

        if (field.fieldType === CrfFieldType.list) {
            if (validateFixedOptions(params.props.value, field.fieldOptions as FieldOption[])) return { ...params };
        }

        if (field.fieldType === CrfFieldType.string) {
            const isValidString = params.props.value.length > 0;
            return isValidString ? { ...params } : { ...params, error: "Value must be a string." };
        }

        const isPositiveFloat = Number(params.props.value) >= 0;
        const error = isPositiveFloat === true ? null : "Value must be positive decimal.";

        return { ...params.props, error };
    };

    const getFieldFormatter = (field: CrfField) => (params: GridValueFormatterParams<string>) => {
        if (field.fieldType === CrfFieldType.date) {
            if (!params.value) return "";
            return new Date(params.value).toLocaleDateString("en-US", { timeZone: "UTC" });
        }
        if (field.fieldType !== CrfFieldType.list) return params.value;

        const options = field.fieldOptions;

        const selectableOptions = options.filter(
            (option: FieldOption): option is CrfFieldOptionDefaultType => option.type !== OptionType.input
        );

        const selectedOption = selectableOptions.find((option) => option.value === params.value);

        if (selectedOption) {
            return selectedOption.label;
        }

        if (field.precision !== undefined) {
            const isNumber = parseFloat(params.value);
            return isNumber ? parseFloat(params.value).toFixed(field.precision) : params.value;
        }

        return params.value;
    };

    // Adjust column width based on content
    const getColumnWidth = (field: CrfField) => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context) {
            context.font = "16px Roboto";
            // Add 20px padding for cell content and 35px for laterality (OU) | (OD) | (OS)
            return Math.ceil(context.measureText(field.fieldName).width) + 20 + 35;
        }
    };

    // Memoized columns based on transpose state
    const displayColumns = useMemo(() => {
        // Check if there are multiple rows per laterality
        const hasMultipleRowsPerLaterality = data.some((reading) => {
            const sameLatRows = data.filter((r) => r.laterality === reading.laterality);
            return sameLatRows.length > 1;
        });

        // Check if there are any conflicts that require adjudication
        const hasAdjudicationNeeded = data.some((reading) => {
            // Get all readings for this laterality
            const readingsForLaterality = data.filter((r) => r.laterality === reading.laterality);

            // Skip if less than 2 readings or if there's a final reading
            if (readingsForLaterality.length < 2 || readingsForLaterality.some((r) => r.final)) {
                return false;
            }

            // Skip if this is the current user's reading
            if (reading.user.email === userData?.email) {
                return false;
            }

            // Use shared utility function to check for conflicts
            return hasAnyConflicts(readingsForLaterality, fields);
        });

        // Only show adjudication column if user is an adjudicator and there are adjudication conflicts
        const showAdjudicationColumn = userHasRole("Adjudicator") && hasAdjudicationNeeded;

        if (!isTransposed) {
            // Original Columns
            const fixedColumns = [
                ...(showAdjudicationColumn
                    ? [
                          {
                              field: "adjudicationNeeded",
                              headerName: "-",
                              width: COLUMN_WIDTHS.adjudicationNeeded,
                              sortable: false,
                              filterable: false,
                              disableColumnMenu: true,
                              hideable: false,
                              renderCell: (params: GridRenderCellParams) => {
                                  const { laterality } = params.row;

                                  // Get all readings for this laterality
                                  const readingsForLaterality = data.filter(
                                      (reading) => reading.laterality === laterality
                                  );

                                  // Skip if less than 2 readings
                                  if (readingsForLaterality.length < 2) return null;

                                  // Check if any readings are marked as final
                                  const hasFinal = readingsForLaterality.some((reading) => reading.final);
                                  if (hasFinal) return null;

                                  // Don't show icon for the current user's own entries
                                  if (params.row.userEmail === userData?.email) return null;

                                  // Use the same hasAnyConflicts utility function for consistency
                                  const hasConflicts = hasAnyConflicts(readingsForLaterality, fields);

                                  // If conflicts found, show warning icon
                                  if (hasConflicts) {
                                      return (
                                          <Tooltip title="Adjudication required">
                                              <WarningAmberIcon color="warning" fontSize="small" />
                                          </Tooltip>
                                      );
                                  }

                                  return null;
                              },
                          },
                      ]
                    : []),
                ...(userHasRole("Project Manager") && hasMultipleRowsPerLaterality
                    ? [
                          {
                              field: "final",
                              headerName: "Final",
                              width: COLUMN_WIDTHS.final,
                              hideable: false,
                              editable: true,
                              type: "boolean",
                              renderCell: (params: GridRenderCellParams) => {
                                  // Get all readings for this laterality
                                  const readingsForLaterality = data.filter(
                                      (reading) => reading.laterality === params.row.laterality
                                  );
                                  const hasMultipleReadingsForThisLaterality = readingsForLaterality.length > 1;

                                  // If only one reading for this laterality, show checked and disabled checkbox
                                  if (!hasMultipleReadingsForThisLaterality || visit.final) {
                                      return <DisabledFinalCheckbox checked={params.row.final} />;
                                  }
                                  const handleFinalChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
                                      event.stopPropagation();

                                      // If visit is final, don't allow changes
                                      if (visit.final) {
                                          return;
                                      }

                                      try {
                                          await setFinal({
                                              patientId: patient.uuid,
                                              finalRequest: {
                                                  visitId: visit.uuid,
                                                  laterality: params.row.laterality,
                                                  userId: params.row.userUuid,
                                              },
                                          }).unwrap();
                                      } catch (error) {
                                          enqueueSnackbar("Failed to update final status", {
                                              variant: "error",
                                          });
                                      }
                                  };

                                  const handleUnsetFinal = async (event: React.ChangeEvent<HTMLInputElement>) => {
                                      event.stopPropagation();

                                      // If visit is final, don't allow changes
                                      if (visit.final) {
                                          return;
                                      }

                                      try {
                                          await unsetFinal({
                                              patientId: patient.uuid,
                                              finalRequest: {
                                                  visitId: visit.uuid,
                                                  laterality: params.row.laterality,
                                                  userId: params.row.userUuid,
                                              },
                                          }).unwrap();
                                      } catch (error) {
                                          enqueueSnackbar("Failed to update final status", {
                                              variant: "error",
                                          });
                                      }
                                  };

                                  if (params.row.final) {
                                      return (
                                          <Checkbox
                                              checked={true}
                                              onChange={handleUnsetFinal}
                                              onClick={(e) => e.stopPropagation()}
                                              icon={<CheckBoxIcon color="success" />}
                                              checkedIcon={<CheckBoxIcon color="success" />}
                                              disabled={visit.final}
                                          />
                                      );
                                  }

                                  // Check if there's another row with same laterality marked as final
                                  const hasFinalForLaterality = data.some(
                                      (reading) =>
                                          reading.laterality === params.row.laterality &&
                                          reading.final &&
                                          reading.user.uuid !== params.row.userUuid
                                  );

                                  if (hasFinalForLaterality) {
                                      return (
                                          <Tooltip title="To mark this reading as final, first unmark the current final reading for this laterality">
                                              <span>
                                                  <CheckBoxOutlineBlankIcon color="disabled" />
                                              </span>
                                          </Tooltip>
                                      );
                                  }

                                  return (
                                      <Checkbox
                                          checked={false}
                                          onChange={handleFinalChange}
                                          onClick={(e) => e.stopPropagation()}
                                          disabled={visit.final}
                                      />
                                  );
                              },
                              sortable: false,
                          },
                      ]
                    : []),
                ...(!userHasRole("Sponsor")
                    ? [
                          {
                              field: "userEmail",
                              headerName: "User",
                              hideable: false,
                              width: COLUMN_WIDTHS.user,
                              renderCell: (params: GridRenderCellParams<string>) => <GridCell {...params} />,
                          },
                      ]
                    : []),
                {
                    field: "laterality",
                    headerName: "Laterality",
                    width: COLUMN_WIDTHS.laterality,
                    hideable: false,
                    valueFormatter: (params: GridValueFormatterParams<string>) => params.value.toUpperCase(),
                    sortable: false,
                },
                {
                    field: "notes",
                    headerName: "Notes",
                    width: 140,
                    renderHeader: () => (
                        <DisplayText
                            style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                            type="bodySmall"
                            text="Notes"
                        />
                    ),
                    editable: true,
                    renderCell: (params: GridRenderCellParams<string>) => {
                        const email = params.row.userEmail;
                        const laterality = params.row.laterality;
                        return (
                            <EditableGridCell
                                {...params}
                                email={email}
                                visitId={visit.uuid}
                                patientId={patient.uuid}
                                laterality={laterality}
                            />
                        );
                    },
                    renderEditCell: (props: GridRenderEditCellParams) => {
                        const email = props.row.userEmail;
                        const laterality = props.row.laterality;

                        const handleUpdate = (newValue: any) => {
                            const payload = {
                                laterality,
                                visitId: visit.uuid,
                                email,
                                notes: newValue,
                            };

                            return editCRF({ patientId: patient.uuid, crfUpdate: payload }).unwrap();
                        };

                        return (
                            <TextAreaEditCell
                                patientId={patient.uuid}
                                {...props}
                                email={email}
                                visitId={visit.uuid}
                                laterality={laterality}
                                handleUpdate={handleUpdate}
                            />
                        );
                    },
                    sortable: false,
                },
            ];

            const metadataColumns = [
                {
                    field: "trialName",
                    headerName: "Trial",
                    renderCell: (params: GridRenderCellParams<string>) => <GridCell {...params} />,
                    hide: true,
                    filterable: false,
                    hideable: false,
                },
                {
                    field: "visitName",
                    headerName: "Visit",
                    renderCell: (params: GridRenderCellParams<string>) => <GridCell {...params} />,
                    hide: true,
                    filterable: false,
                    hideable: false,
                },
                {
                    field: "patientName",
                    headerName: "Patient",
                    renderCell: (params: GridRenderCellParams<string>) => <GridCell {...params} />,
                    hide: true,
                    filterable: false,
                    hideable: false,
                },
                {
                    field: "siteName",
                    headerName: "Site",
                    renderCell: (params: GridRenderCellParams<string>) => <GridCell {...params} />,
                    hide: true,
                    filterable: false,
                    hideable: false,
                },
            ];

            const fieldColumns = fields.map((field) => ({
                field: field.fieldName,
                headerName: field.fieldName,
                minWidth: getColumnWidth(field),
                flex: 1,
                renderHeader: () => <ColumnHeader visit={visit} field={field} />,
                valueFormatter: getFieldFormatter(field),
                hide: !field.isRequired,
                hideable: true,
                editable: true,
                preProcessEditCellProps: getFieldValidator(field),
                renderEditCell: renderEditCell(field),
                renderCell: renderCell(field),
                sortable: false,
            }));

            return [...fixedColumns, ...fieldColumns, ...metadataColumns];
        }
        // Transposed
        else {
            const baseColumns = [
                {
                    field: "attribute",
                    headerName: "Attribute",
                    width: 200,
                    sortable: false,
                    pinned: "left",
                },
            ];

            const dynamicColumns = data.map((crfReading) => {
                // Check if this column needs adjudication (similar logic to the row indicator)
                const readingsForLaterality = data.filter((reading) => reading.laterality === crfReading.laterality);
                let needsAdjudication = false;

                // Only check if there are multiple readings and no finals
                if (readingsForLaterality.length >= 2) {
                    const hasFinal = readingsForLaterality.some((reading) => reading.final);

                    // Only check for conflicts if no finals and it's not the current user's entry
                    if (!hasFinal && crfReading.user.email !== userData?.email) {
                        // Check for conflicts
                        needsAdjudication = fields.some((field) => {
                            const values = new Set();
                            for (const reading of readingsForLaterality) {
                                const fieldValue = reading.values.find(
                                    (val) => val.fieldName === field.fieldName
                                )?.fieldValue;
                                if (fieldValue !== undefined) {
                                    values.add(fieldValue);
                                }
                            }
                            return values.size > 1;
                        });
                    }
                }

                return {
                    field: `${crfReading.user.uuid}-${crfReading.laterality}-${visit.name}`,
                    headerName: `${crfReading.user.email} - (${crfReading.laterality})`,
                    flex: 1,
                    minWidth: 200,
                    maxWidth: 400,
                    sortable: false,
                    filterable: false,
                    editable: crfReading.isEditable,
                    renderEditCell: (params: GridRenderEditCellParams) => {
                        const fieldName = params.id;
                        const field = fields.find((f) => f.fieldName === fieldName) as CrfField;
                        const laterality = crfReading.laterality;
                        const userEmail = crfReading.user.email;

                        if (params.id === "notes") {
                            const handleUpdate = (newValue: any) => {
                                const payload = {
                                    laterality,
                                    visitId: visit.uuid,
                                    email: userEmail,
                                    notes: newValue,
                                };

                                return editCRF({ patientId: patient.uuid, crfUpdate: payload }).unwrap();
                            };

                            return (
                                <TextAreaEditCell
                                    {...params}
                                    patientId={patient.uuid}
                                    email={userEmail}
                                    visitId={visit.uuid}
                                    laterality={laterality}
                                    handleUpdate={handleUpdate}
                                />
                            );
                        }

                        return renderEditCell(field, laterality, userEmail)(params);
                    },
                    renderCell: (params: GridRenderCellParams) => {
                        const fieldName = params.id;
                        const field = fields.find((f) => f.fieldName === fieldName) as CrfField;

                        if (fieldName === "final") {
                            // Get all readings for this laterality
                            const readingsForLaterality = data.filter(
                                (reading) => reading.laterality === crfReading.laterality
                            );
                            const hasMultipleReadingsForThisLaterality = readingsForLaterality.length > 1;

                            // If only one reading for this laterality or visit is final, show checked and disabled checkbox
                            if (!hasMultipleReadingsForThisLaterality || visit.final) {
                                return <DisabledFinalCheckbox checked={params.row[params.field]} />;
                            }
                            const handleFinalChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
                                event.stopPropagation();

                                // If visit is final, don't allow changes
                                if (visit.final) {
                                    return;
                                }

                                try {
                                    await setFinal({
                                        patientId: patient.uuid,
                                        finalRequest: {
                                            visitId: visit.uuid,
                                            laterality: crfReading.laterality,
                                            userId: crfReading.user.uuid,
                                        },
                                    }).unwrap();
                                } catch (error) {
                                    enqueueSnackbar("Failed to update final status", {
                                        variant: "error",
                                    });
                                }
                            };

                            const handleUnsetFinal = async (event: React.ChangeEvent<HTMLInputElement>) => {
                                event.stopPropagation();

                                // If visit is final, don't allow changes
                                if (visit.final) {
                                    return;
                                }

                                try {
                                    await unsetFinal({
                                        patientId: patient.uuid,
                                        finalRequest: {
                                            visitId: visit.uuid,
                                            laterality: crfReading.laterality,
                                            userId: crfReading.user.uuid,
                                        },
                                    }).unwrap();
                                } catch (error) {
                                    enqueueSnackbar("Failed to update final status", {
                                        variant: "error",
                                    });
                                }
                            };

                            if (params.row[params.field]) {
                                return (
                                    <Checkbox
                                        checked={true}
                                        onChange={handleUnsetFinal}
                                        onClick={(e) => e.stopPropagation()}
                                        icon={<CheckBoxIcon color="success" />}
                                        checkedIcon={<CheckBoxIcon color="success" />}
                                        disabled={visit.final}
                                    />
                                );
                            }
                            // Check if there's another row with same laterality marked as final
                            const hasFinalForLaterality = data.some(
                                (reading) =>
                                    reading.laterality === crfReading.laterality &&
                                    reading.final &&
                                    reading.user.uuid !== crfReading.user.uuid
                            );

                            if (hasFinalForLaterality) {
                                return (
                                    <Tooltip title="To mark this reading as final, first unmark the current final reading for this laterality">
                                        <span>
                                            <CheckBoxOutlineBlankIcon color="disabled" />
                                        </span>
                                    </Tooltip>
                                );
                            }

                            return (
                                <Checkbox
                                    checked={false}
                                    onChange={handleFinalChange}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={visit.final}
                                />
                            );
                        }

                        const laterality = crfReading.laterality;
                        const userEmail = crfReading.user.email;

                        return renderCell(field, laterality, userEmail)(params);
                    },
                    valueFormatter: (params: GridValueFormatterParams<string>) => {
                        const fieldName = params.id;

                        // Special handling for notes and final
                        if (fieldName === "notes") return params.value;
                        if (fieldName === "final") {
                            return params.value ? "Yes" : "No";
                        }

                        // Only look for field definition for regular fields
                        const field = fields.find((f) => f.fieldName === fieldName) as CrfField;
                        if (!field) return params.value;

                        return getFieldFormatter(field)(params);
                    },
                    renderHeader: () => (
                        <Box sx={{ textAlign: "left", display: "flex", alignItems: "center" }}>
                            <TwoLineHeader
                                visit={visit}
                                email={crfReading.user.email}
                                laterality={crfReading.laterality}
                                needsAdjudication={needsAdjudication}
                            />
                        </Box>
                    ),
                };
            });

            return [...baseColumns, ...dynamicColumns];
        }
    }, [fields, data, userHasRole, visit.uuid, isTransposed, userData?.email]);

    // Memoized rows based on transpose state
    const displayRows = useMemo(() => {
        if (!isTransposed) {
            // Original Rows
            return [...data]
                .sort((a, b) => a.user.email.localeCompare(b.user.email) || a.laterality.localeCompare(b.laterality))
                .map((crfReading: CrfReading) => ({
                    id: `${crfReading.user.uuid}-${crfReading.laterality}-${visit.name}`,
                    trialName: selectedTrial!.name,
                    visitName: visit.name,
                    siteName: patient.site,
                    patientName: patient.patientId,
                    isEditable: crfReading.isEditable,
                    laterality: crfReading.laterality,
                    notes: crfReading.notes,
                    patientId: patient.uuid,
                    visitId: visit.uuid,
                    userEmail: crfReading.user.email,
                    userUuid: crfReading.user.uuid,
                    final: crfReading.final,
                    adjudicationNeeded: "", // Add placeholder for adjudication indicator
                    // Dynamically add field values
                    // @ts-ignore
                    ...crfReading.values.reduce(
                        (acc: Record<string, any>, readingValue: CrfReadingValue) => ({
                            ...acc,
                            [readingValue.fieldName]: readingValue.fieldValue,
                        }),
                        {} as Record<string, any>
                    ),
                }));
        } else {
            const hasMultipleRowsPerLaterality = data.some((reading) => {
                const sameLatRows = data.filter((r) => r.laterality === reading.laterality);
                return sameLatRows.length > 1;
            });

            const analytesFields = fields
                .filter((field) => !["userEmail", "laterality"].includes(field.fieldName) && field.isRequired)
                .map((field) => field.fieldName);

            // Only include final in the fields if user is PM and has multiple rows per laterality
            const rowFields = [
                ...(userHasRole("Project Manager") && hasMultipleRowsPerLaterality ? ["final"] : []),
                "notes",
                ...analytesFields,
            ];

            const transposedRows = rowFields.map((fieldName) => ({
                id: fieldName,
                attribute:
                    fieldName === "notes"
                        ? fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
                        : fieldName === "final"
                        ? "Final"
                        : fieldName,
                isEditable: fieldName !== "final",
                ...data.reduce((acc, crfReading) => {
                    const key = `${crfReading.user.uuid}-${crfReading.laterality}-${visit.name}`;
                    let fieldValue;
                    if (fieldName === "notes") {
                        fieldValue = crfReading.notes;
                    } else if (fieldName === "final") {
                        fieldValue = crfReading.final;
                    } else {
                        fieldValue = crfReading.values.find((val) => val.fieldName === fieldName)?.fieldValue || "";
                    }
                    acc[key] = fieldValue;
                    return acc;
                }, {} as Record<string, any>),
            }));

            return transposedRows;
        }
    }, [patient, visit, selectedTrial, isTransposed, data, fields]);

    // Function to toggle transpose state
    const transposeTable = useCallback(() => {
        setIsTransposed((prev) => !prev);
    }, []);

    // Cell Editability
    const isCellEditable = useCallback((params: any) => {
        return params.row.isEditable;
    }, []);

    // Grid Features
    const features = { newEditingApi: true };

    const getFileName = () => {
        const userId = userData!.userID;
        const visitNumber = visit.name;
        const date = new Date().toJSON().slice(0, 10);

        return `${selectedTrial!.name}_${userId}_${visitNumber}_${date}`;
    };

    const fileName = getFileName();

    const csvOptions = {
        fileName,
        allColumns: true,
    };

    // Move hasMultipleRowsPerLaterality check outside of useMemo to reuse it
    const hasMultipleRowsPerLaterality = useMemo(() => {
        return data.some((reading) => {
            const sameLatRows = data.filter((r) => r.laterality === reading.laterality);
            return sameLatRows.length > 1;
        });
    }, [data]);

    const userRoles = {
        isProjectManager: userHasRole("Project Manager"),
        isSponsor: userHasRole("Sponsor"),
    };

    // Check if adjudication is possible for each laterality
    const canAdjudicate = useMemo(() => {
        // Only show adjudication buttons for users with Adjudicator role
        if (!userHasRole("Adjudicator") || visit.final) {
            return {
                OS: { canAdjudicate: false, hasConflicts: false },
                OD: { canAdjudicate: false, hasConflicts: false },
            };
        }

        // Check each laterality
        const result = {
            OS: { canAdjudicate: false, hasConflicts: false },
            OD: { canAdjudicate: false, hasConflicts: false },
        };

        // For OS laterality
        const osReadings = data.filter((reading) => reading.laterality === Laterality.OS);
        if (osReadings.length >= 2) {
            // Check if none are marked as final
            const hasFinal = osReadings.some((reading) => reading.final);
            if (!hasFinal) {
                // We can adjudicate if there are at least 2 readings with no final
                result.OS.canAdjudicate = true;
                // Check if there are conflicts to determine label
                result.OS.hasConflicts = hasAnyConflicts(osReadings, fields);
            }
        }

        // For OD laterality
        const odReadings = data.filter((reading) => reading.laterality === Laterality.OD);
        if (odReadings.length >= 2) {
            // Check if none are marked as final
            const hasFinal = odReadings.some((reading) => reading.final);
            if (!hasFinal) {
                // We can adjudicate if there are at least 2 readings with no final
                result.OD.canAdjudicate = true;
                // Check if there are conflicts to determine label
                result.OD.hasConflicts = hasAnyConflicts(odReadings, fields);
            }
        }

        return result;
    }, [data, userHasRole, visit.final, fields]);

    // Handle adjudication
    const handleAdjudicate = useCallback((laterality: Laterality) => {
        setSelectedLaterality(laterality);
        setAdjudicationDialogOpen(true);
    }, []);

    // Calculate if adjudication column should be shown
    const showAdjudicationColumn = useMemo(
        () => userHasRole("Adjudicator") && (canAdjudicate.OS.hasConflicts || canAdjudicate.OD.hasConflicts),
        [userHasRole, canAdjudicate]
    );

    // Generate all styles using our consolidated function
    const tableStyles = useMemo(
        () => generateTableStyles(userRoles, hasMultipleRowsPerLaterality, showAdjudicationColumn, isTransposed),
        [userRoles, hasMultipleRowsPerLaterality, showAdjudicationColumn, isTransposed]
    );

    // Use the generated cell class name function
    const getCellClassName = useCallback(
        (params: GridCellParams) => tableStyles.getCellClassNameFn(params),
        [tableStyles]
    );

    // Scroll handling with dynamic fixed column fields
    const handleScrollHorizontal = useCallback(() => {
        const virtualScrollerElement = document.querySelector(`#${tableUniqueId} .MuiDataGrid-virtualScroller`);
        if (virtualScrollerElement) {
            const currentScrollPos = virtualScrollerElement.scrollLeft;

            // Create selector that excludes all fixed column fields
            const excludeSelectors = tableStyles.fixedColumnFields
                .map((field) => `:not([data-field="${field}"])`)
                .join("");

            const columnsHeaders = document.querySelectorAll(
                `#${tableUniqueId} .MuiDataGrid-columnHeader${excludeSelectors}`
            );

            columnsHeaders.forEach((columnHeader: any) => {
                columnHeader.style.transform = `translate3d(-${currentScrollPos}px, 0px, 0px)`;
            });
        }
    }, [tableUniqueId, tableStyles.fixedColumnFields]);

    useEffect(() => {
        const findVirtualScroller = () => {
            const virtualScrollerElement = document.querySelector(`#${tableUniqueId} .MuiDataGrid-virtualScroller`);
            if (!virtualScrollerElement) {
                setTimeout(findVirtualScroller, 100);
            } else {
                virtualScrollerElement.addEventListener("scroll", handleScrollHorizontal);
                return () => {
                    virtualScrollerElement.removeEventListener("scroll", handleScrollHorizontal);
                };
            }
        };
        findVirtualScroller();
    }, [tableUniqueId, handleScrollHorizontal, isTransposed]);

    // Custom toolbar component with Transpose button
    const getCustomToolbar =
        ({
            isTransposed,
            onTranspose,
            canAdjudicateOS = { canAdjudicate: false, hasConflicts: false },
            canAdjudicateOD = { canAdjudicate: false, hasConflicts: false },
            onAdjudicate = () => {},
        }: {
            isTransposed: boolean;
            onTranspose: () => void;
            canAdjudicateOS?: { canAdjudicate: boolean; hasConflicts: boolean };
            canAdjudicateOD?: { canAdjudicate: boolean; hasConflicts: boolean };
            onAdjudicate?: (laterality: Laterality) => void;
        }) =>
        () => {
            const needsAdjudication = canAdjudicateOS.canAdjudicate || canAdjudicateOD.canAdjudicate;
            const hasConflicts = canAdjudicateOS.hasConflicts || canAdjudicateOD.hasConflicts;
            const lateralityToAdjudicate = canAdjudicateOS.canAdjudicate
                ? Laterality.OS
                : canAdjudicateOD.canAdjudicate
                ? Laterality.OD
                : null;

            return (
                <GridToolbarContainer>
                    {needsAdjudication && lateralityToAdjudicate && onAdjudicate && (
                        <Button
                            onClick={() => onAdjudicate(lateralityToAdjudicate)}
                            color={hasConflicts ? "warning" : "primary"}
                            size="small"
                            sx={{ ml: 1 }}
                            startIcon={<MergeIcon />}
                        >
                            {hasConflicts ? "Adjudication Required" : "Adjudicate"}
                        </Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />

                    <Button
                        onClick={onTranspose}
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                        startIcon={<PivotTableChartIcon />}
                    >
                        {isTransposed ? "Revert Transpose" : "Transpose"}
                    </Button>
                    <GridToolbarExport />
                </GridToolbarContainer>
            );
        };

    return (
        <Box
            sx={{
                // maxHeight: 800,
                ...baseStyles,
                ...tableStyles.positionStyles,
                ...tableStyles.transposedStyles,
            }}
            id={tableUniqueId}
        >
            <DataGrid
                key={isTransposed ? "transposed" : "original"}
                columns={displayColumns}
                sx={tableStyles.headerStyles}
                disableVirtualization
                rows={displayRows}
                autoHeight
                componentsProps={{
                    toolbar: {
                        csvOptions,
                        isTransposed,
                        onTranspose: transposeTable,
                        canAdjudicateOS: canAdjudicate.OS,
                        canAdjudicateOD: canAdjudicate.OD,
                        onAdjudicate: handleAdjudicate,
                    },
                }}
                components={{
                    Toolbar: getCustomToolbar({
                        isTransposed,
                        onTranspose: transposeTable,
                        canAdjudicateOS: canAdjudicate.OS,
                        canAdjudicateOD: canAdjudicate.OD,
                        onAdjudicate: handleAdjudicate,
                    }),
                }}
                getCellClassName={getCellClassName}
                isCellEditable={isCellEditable}
                experimentalFeatures={features}
                columnBuffer={displayColumns.length}
                density="compact"
                disableColumnFilter={false}
                disableColumnMenu
                disableDensitySelector
                disableSelectionOnClick
                hideFooter
            />

            {/* Adjudication Dialog */}
            {adjudicationDialogOpen && (
                <AdjudicationDialog
                    open={adjudicationDialogOpen}
                    onClose={() => {
                        setAdjudicationDialogOpen(false);
                        setSelectedLaterality(null);
                    }}
                    laterality={selectedLaterality}
                    readings={data}
                    fields={fields}
                    patientId={patient.uuid}
                    visitId={visit.uuid}
                />
            )}
        </Box>
    );
};

export default CRFVisitTable;
